import { useCallback, useMemo } from 'react';

import { resolveVoucherDownloadTarget, type Voucher } from '@src/domains/vouchers';
import { useVoucherDownload } from '@src/features/vouchers/download/hooks/useVoucherDownload';

interface Params {
  reservationId: string | null;
  vouchers: Voucher[];
}

export function useReservationVoucherDownload(params: Params) {
  const { reservationId, vouchers } = params;
  const { download, isDownloading } = useVoucherDownload();

  const targets = useMemo(() => resolveVoucherDownloadTarget({ reservationId, vouchers }), [reservationId, vouchers]);
  const hasDownloadableTargets = targets.length > 0;

  const downloadAll = useCallback(async () => {
    for (const target of targets) {
      await download(target);
    }
  }, [targets, download]);

  const downloadOne = useCallback((voucher: Voucher) => download({ kind: 'voucher', id: voucher.id }), [download]);

  const isDownloadingAll = targets.some((target) => isDownloading(target.id));

  const isDownloadingOne = useCallback((voucher: Voucher) => isDownloading(voucher.id), [isDownloading]);

  return { downloadAll, downloadOne, isDownloadingAll, isDownloadingOne, hasDownloadableTargets };
}
